import { clear, el } from "./components/dom.js";
import { installDiagnostics } from "./utils/diagnostics.js";
import { installPullToRefresh } from "./utils/pull-refresh.js";
import { renderHome } from "./views/home.js";
import { renderGamesList } from "./views/games-list.js";
import { renderAddGame } from "./views/add-game.js";
import { renderGameDetail } from "./views/game-detail.js";
import { renderReleaseSchedule } from "./views/release-schedule.js";
import { renderBooksList } from "./views/books-list.js";
import { renderAddBook } from "./views/add-book.js";
import { renderBookDetail } from "./views/book-detail.js";
import { renderTvList } from "./views/tv-list.js";
import { renderAddTv } from "./views/add-tv.js";
import { renderTvDetail } from "./views/tv-detail.js";
import { renderSeasonDetail } from "./views/season-detail.js";
import { renderAnimeList } from "./views/anime-list.js";
import { renderAddAnime } from "./views/add-anime.js";
import { renderAnimeDetail } from "./views/anime-detail.js";
import { renderAnimeSeasonDetail } from "./views/anime-season-detail.js";
import { renderMoviesList } from "./views/movies-list.js";
import { renderAddMovie } from "./views/add-movie.js";
import { renderMovieDetail } from "./views/movie-detail.js";
import { renderComicsList } from "./views/comics-list.js";
import { renderAddComic } from "./views/add-comic.js";
import { renderComicDetail } from "./views/comic-detail.js";
import { runSubscriptionChecks } from "./db/comic-subscriptions.js";
import { runPeopleChecks } from "./db/people-watch.js";
import { runNewsBell } from "./db/news-bell.js";
import { loadPersonFacets } from "./db/people.js";
import { loadTagFacets, loadAiringFacets } from "./db/media.js";
import { installButtonRoles } from "./utils/a11y.js";
import { renderGroupsList } from "./views/groups-list.js";
import { renderGroupDetail } from "./views/group-detail.js";
import { renderSignIn } from "./views/sign-in.js";
import { renderAccount } from "./views/account.js";
import { onAuthChange, getSession } from "./db/auth.js";
import { initSync, setRefreshView, getSyncState, SYNC_EVENT, syncNow } from "./db/sync.js";
import { maybeShowWhatsNew } from "./components/whats-new.js";
import { notificationsPanel } from "./components/notifications-panel.js";
import {
	scanReleaseNotifications,
	pruneNotifications,
	unreadCount,
	NOTIF_EVENT,
} from "./db/notifications.js";
import {
	runDailyCatalogRefresh,
	dailyRefreshDue,
} from "./db/catalog-refresh.js";
import { applySkinOnBoot } from "./utils/skin.js";

// Rarely used screens load on demand (their own chunks) instead of in the
// startup bundle. A chunk that can't load — offline right after a deploy —
// shows a message instead of a blank screen.
function staleBuildReload() {
	try {
		const last = Number(sessionStorage.getItem("staleBuildReloadAt") ?? 0);
		if (Date.now() - last < 60_000) return false;
		sessionStorage.setItem("staleBuildReloadAt", String(Date.now()));
	} catch {
		return false;
	}
	window.location.reload();
	return true;
}

function lazyView(load, name) {
	return async (root, ...args) => {
		let mod;
		// A slow first load (a cold network) shows a placeholder, not a blank screen.
		const placeholder = el("div.view", {}, el("p", { style: { padding: "90px 16px", textAlign: "center", color: "#9a9aa2" } }, "Loading…"));
		const timer = setTimeout(() => root.append(placeholder), 200);
		try {
			mod = await load();
			clearTimeout(timer);
			placeholder.remove();
		} catch (err) {
			clearTimeout(timer);
			placeholder.remove();
			console.warn(`Couldn't load the ${name} screen:`, err);
			// Online, the usual cause is a deploy since this page loaded: the old
			// build's screen files are gone. Reload onto the new build (once a
			// minute at most, so a real outage can't loop).
			if (navigator.onLine && staleBuildReload()) return;
			root.append(
				el(
					"div.view",
					{},
					el("p", { style: { padding: "90px 16px", textAlign: "center" } }, "This screen couldn't load. Check your connection and reload."),
				),
			);
			return;
		}
		return mod[name](root, ...args);
	};
}
const renderStats = lazyView(() => import("./views/stats.js"), "renderStats");
const renderVsBattle = lazyView(() => import("./views/vs-battle.js"), "renderVsBattle");
const renderHistory = lazyView(() => import("./views/history.js"), "renderHistory");
const renderYearReview = lazyView(() => import("./views/year-review.js"), "renderYearReview");
const renderImport = lazyView(() => import("./views/import.js"), "renderImport");
const renderSettings = lazyView(() => import("./views/settings.js"), "renderSettings");
const renderShared = lazyView(() => import("./views/shared.js"), "renderShared");
const renderNewsstand = lazyView(() => import("./views/newsstand.js"), "renderNewsstand");
const renderPeople = lazyView(() => import("./views/people.js"), "renderPeople");
const renderPerson = lazyView(() => import("./views/person.js"), "renderPerson");
const renderRecommendations = lazyView(() => import("./views/recommendations.js"), "renderRecommendations");
const renderWanted = lazyView(() => import("./views/wanted.js"), "renderWanted");
const renderBrowse = lazyView(() => import("./views/browse.js"), "renderBrowse");
const renderBookStats = lazyView(() => import("./views/book-stats.js"), "renderBookStats");
const renderBookSeries = lazyView(() => import("./views/book-series.js"), "renderBookSeries");
const renderGameSeries = lazyView(() => import("./views/games-series.js"), "renderGameSeries");
const renderComicCreators = lazyView(() => import("./views/comic-creators.js"), "renderComicCreators");
const renderComicCreator = lazyView(() => import("./views/comic-creator.js"), "renderComicCreator");
const renderComicSeries = lazyView(() => import("./views/comic-series.js"), "renderComicSeries");
const renderComicSubscriptions = lazyView(() => import("./views/comic-subscriptions.js"), "renderComicSubscriptions");
const renderGroupNew = lazyView(() => import("./views/group-new.js"), "renderGroupNew");
const renderSignUp = lazyView(() => import("./views/sign-up.js"), "renderSignUp");
const renderForgotPassword = lazyView(() => import("./views/forgot-password.js"), "renderForgotPassword");
const renderResetPassword = lazyView(() => import("./views/reset-password.js"), "renderResetPassword");
const renderMigrate = lazyView(() => import("./views/migrate.js"), "renderMigrate");
const renderSearch = lazyView(() => import("./views/search.js"), "renderSearch");
const renderBacklog = lazyView(() => import("./views/backlog.js"), "renderBacklog");

const routes = [
	{ pattern: /^\/?$/, render: renderHome },
	{ pattern: /^\/games\/?$/, render: renderGamesList },
	{ pattern: /^\/games\/add\/?$/, render: renderAddGame },
	{ pattern: /^\/games\/browse\/?$/, render: renderBrowse },
	{ pattern: /^\/games\/stats\/?$/, render: renderStats },
	{ pattern: /^\/games\/vs\/?$/, render: (root) => renderVsBattle(root, "videogame") },
	{ pattern: /^\/games\/series\/([^/]+)\/?$/, render: renderGameSeries },
	{ pattern: /^\/upcoming\/?$/, render: renderReleaseSchedule },
	{ pattern: /^\/history\/?$/, render: renderHistory },
	{ pattern: /^\/year(?:\/(\d{4}))?\/?$/, render: (root, _match, year) => renderYearReview(root, year) },
	{ pattern: /^\/import\/?$/, render: renderImport },
	{ pattern: /^\/settings\/?$/, render: renderSettings },
	{ pattern: /^\/shared\/([A-Za-z0-9]{16,64})\/?$/, render: (root, _match, token) => renderShared(root, token) },
	{ pattern: /^\/news\/?$/, render: renderNewsstand },
	{ pattern: /^\/search\/?$/, render: renderSearch },
	{ pattern: /^\/backlog\/?$/, render: renderBacklog },
	{ pattern: /^\/people\/?$/, render: renderPeople },
	{
		pattern: /^\/people\/(wikidata|tvmaze|openlibrary|igdb)\/([^/]+)\/?$/,
		render: (root, _match, source, id) => renderPerson(root, source, id),
	},
	{ pattern: /^\/recommendations\/?$/, render: renderRecommendations },
	{ pattern: /^\/wanted\/?$/, render: renderWanted },
	{
		pattern: /^\/games\/([^/]+)\/?$/,
		render: (root, _match, id) => renderGameDetail(root, id),
	},
	{ pattern: /^\/books\/?$/, render: renderBooksList },
	{ pattern: /^\/books\/add\/?$/, render: renderAddBook },
	{ pattern: /^\/books\/stats\/?$/, render: renderBookStats },
	{ pattern: /^\/books\/vs\/?$/, render: (root) => renderVsBattle(root, "book") },
	{
		pattern: /^\/books\/series\/([^/]+)\/?$/,
		render: renderBookSeries,
	},
	{
		pattern: /^\/books\/([^/]+)\/?$/,
		render: (root, _match, id) => renderBookDetail(root, id),
	},
	{ pattern: /^\/tv\/?$/, render: renderTvList },
	{ pattern: /^\/tv\/add\/?$/, render: renderAddTv },
	{ pattern: /^\/tv\/vs\/?$/, render: (root) => renderVsBattle(root, "tv") },
	{
		pattern: /^\/tv\/([^/]+)\/season\/([^/]+)\/?$/,
		render: renderSeasonDetail,
	},
	{
		pattern: /^\/tv\/([^/]+)\/?$/,
		render: (root, _match, id) => renderTvDetail(root, id),
	},
	{ pattern: /^\/anime\/?$/, render: renderAnimeList },
	{ pattern: /^\/anime\/add\/?$/, render: renderAddAnime },
	{ pattern: /^\/anime\/vs\/?$/, render: (root) => renderVsBattle(root, "anime") },
	{
		pattern: /^\/anime\/([^/]+)\/season\/([^/]+)\/?$/,
		render: renderAnimeSeasonDetail,
	},
	{
		pattern: /^\/anime\/([^/]+)\/?$/,
		render: (root, _match, id) => renderAnimeDetail(root, id),
	},
	{ pattern: /^\/movies\/?$/, render: renderMoviesList },
	{ pattern: /^\/movies\/add\/?$/, render: renderAddMovie },
	{ pattern: /^\/movies\/vs\/?$/, render: (root) => renderVsBattle(root, "movie") },
	{
		pattern: /^\/movies\/([^/]+)\/?$/,
		render: (root, _match, id) => renderMovieDetail(root, id),
	},
	{ pattern: /^\/comics\/?$/, render: renderComicsList },
	{ pattern: /^\/comics\/add\/?$/, render: renderAddComic },
	{ pattern: /^\/comics\/vs\/?$/, render: (root) => renderVsBattle(root, "comic") },
	// Before /comics/:id, which would otherwise read "creators" as a comic id.
	{ pattern: /^\/comics\/creators\/?$/, render: renderComicCreators },
	{ pattern: /^\/comics\/subscriptions\/?$/, render: renderComicSubscriptions },
	{
		pattern: /^\/comics\/series\/(\d+)\/?$/,
		render: (root, _match, id) => renderComicSeries(root, id),
	},
	{
		pattern: /^\/comics\/creator\/(\d+)\/?$/,
		render: (root, _match, id) => renderComicCreator(root, id),
	},
	{
		pattern: /^\/comics\/([^/]+)\/?$/,
		render: (root, _match, id) => renderComicDetail(root, id),
	},
	{ pattern: /^\/groups\/?$/, render: renderGroupsList },
	{ pattern: /^\/groups\/new\/?$/, render: renderGroupNew },
	{
		pattern: /^\/groups\/([^/]+)\/?$/,
		render: (root, _match, id) => renderGroupDetail(root, id),
	},
	{ pattern: /^\/sign-in\/?$/, render: renderSignIn },
	{ pattern: /^\/sign-up\/?$/, render: renderSignUp },
	{ pattern: /^\/forgot-password\/?$/, render: renderForgotPassword },
	{ pattern: /^\/reset-password\/?$/, render: renderResetPassword },
	{ pattern: /^\/account\/?$/, render: renderAccount },
	{ pattern: /^\/migrate\/?$/, render: renderMigrate },
];

// Routes reachable without a session. Everything else requires login (the app is
// account-based as of Phase 5). reset-password is here because it's opened from
// the recovery email (under a temporary recovery session, but treated as public).
const PUBLIC_ROUTES = [
	/^\/sign-in\/?$/,
	/^\/sign-up\/?$/,
	/^\/forgot-password\/?$/,
	/^\/reset-password\/?$/,
	// A shared collection link works signed out (roadmap #58).
	/^\/shared\/[A-Za-z0-9]{16,64}\/?$/,
];

function isPublicPath(path) {
	return PUBLIC_ROUTES.some((re) => re.test(path));
}

// Current auth state, kept in sync via onAuthChange. The gate in dispatch() reads
// this; it's seeded from getSession() before the first dispatch on load.
let authed = false;

function getHashPath() {
	const raw = window.location.hash.slice(1) || "/";
	const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
	// Strip any query string ("/games?genre=RPG" → "/games") before matching.
	return withSlash.split("?")[0];
}

// Parse the query string of the current hash (e.g. "#/games?genre=RPG").
export function getQueryParams() {
	const raw = window.location.hash.slice(1);
	const qIndex = raw.indexOf("?");
	return new URLSearchParams(qIndex >= 0 ? raw.slice(qIndex + 1) : "");
}

// A small in-app navigation stack so "‹" can retrace the actual path the user
// took (including query strings / filters), rather than a fixed parent link.
// Capped so it can't grow without bound.
const navStack = [];
const NAV_STACK_MAX = 25;

function currentHashFull() {
	const raw = window.location.hash.slice(1) || "/";
	return raw.startsWith("/") ? raw : `/${raw}`;
}

function recordNav(fullPath) {
	if (navStack[navStack.length - 1] === fullPath) return; // reload / no-op
	if (navStack[navStack.length - 2] === fullPath) {
		navStack.pop(); // stepped back to the previous page
		return;
	}
	navStack.push(fullPath);
	if (navStack.length > NAV_STACK_MAX) navStack.shift();
}

// Go to the previous page in the in-app stack, or `fallback` if there's nothing
// behind us (e.g. the app was opened directly onto this page).
export function navigateBack(fallback = "/") {
	navigate(navStack[navStack.length - 2] ?? fallback);
}

function dispatch() {
	const root = document.getElementById("app");
	const path = getHashPath();

	// Login gate: the app is account-based. Send signed-out users to sign-in for
	// any non-public route, and bounce already-signed-in users off the sign-in/up
	// screens back into the app.
	if (!authed && !isPublicPath(path)) {
		navigate("/sign-in");
		return;
	}
	if (authed && (/^\/sign-in\/?$/.test(path) || /^\/sign-up\/?$/.test(path))) {
		navigate("/");
		return;
	}

	recordNav(currentHashFull());
	clear(root);
	// Default: every navigation starts at the top. Views that want to restore
	// a remembered scroll position (e.g. games-list after Back from a game)
	// can override after their async content has rendered.
	window.scrollTo(0, 0);
	updateTopBar(path);

	for (const route of routes) {
		const match = path.match(route.pattern);
		if (match) {
			route.render(root, match, ...match.slice(1));
			return;
		}
	}
	root.append(notFound());
}

function notFound() {
	const view = el("div.view", {});
	const card = el(
		"div",
		{
			style: {
				textAlign: "center", padding: "40px 24px", margin: "24px 0", borderRadius: "14px",
				background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.14)",
				backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)",
			},
		},
		el("div", {
			style: { fontFamily: "'Anton', sans-serif", fontSize: "40px", textTransform: "uppercase", color: "#fff", lineHeight: ".9" },
		}, "Not found"),
		el("button", {
			type: "button",
			onclick: () => navigate("/"),
			style: {
				marginTop: "18px", fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "11px",
				letterSpacing: ".06em", textTransform: "uppercase", color: "#fff",
				background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.2)",
				borderRadius: "999px", padding: "11px 18px", cursor: "pointer",
			},
		}, "Back home"),
	);
	view.append(card);
	return view;
}

// Persistent top app bar: Menu (left, opens the category drawer) · "My Media"
// wordmark (centre, taps home) · Account (right). Created once and kept as a
// sibling of #app so the per-navigation clear(#app) never removes it. Each
// screen's own header (back / title / actions) sits below this strip.
let topBar = null;
let notifPanel = null;

function ensureTopBar() {
	if (topBar) return topBar;
	topBar = document.createElement("nav");
	topBar.className = "topbar";
	topBar.hidden = true;
	topBar.innerHTML = `
		<button class="topbar__btn" type="button" data-nav="menu" aria-label="Menu" aria-haspopup="true" aria-expanded="false">
			<svg class="topbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
		</button>
		<button class="topbar__title" type="button" data-nav="home">MEDIA<span class="topbar__lime">TRACKER</span></button>
		<button class="topbar__btn" type="button" data-nav="search" aria-label="Search your library">
			<svg class="topbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>
		</button>
		<button class="topbar__btn topbar__btn--bell" type="button" data-action="notifications" aria-label="Notifications">
			<svg class="topbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
			<span class="topbar__badge" hidden></span>
		</button>
		<button class="topbar__btn" type="button" data-nav="account" aria-label="Account">
			<svg class="topbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></svg>
			<span class="topbar__sync" hidden></span>
		</button>`;
	topBar
		.querySelector('[data-nav="menu"]')
		.addEventListener("click", openDrawer);
	topBar
		.querySelector('[data-nav="home"]')
		.addEventListener("click", () => navigate("/"));
	topBar
		.querySelector('[data-nav="account"]')
		.addEventListener("click", () => navigate("/account"));
	topBar
		.querySelector('[data-nav="search"]')
		.addEventListener("click", () => navigate("/search"));
	window.addEventListener(SYNC_EVENT, refreshSyncDot);
	window.addEventListener("online", refreshSyncDot);
	window.addEventListener("offline", refreshSyncDot);

	// Notifications bell: toggles its dropdown panel (not a route).
	notifPanel = notificationsPanel();
	document.body.appendChild(notifPanel.el);
	topBar
		.querySelector('[data-action="notifications"]')
		.addEventListener("click", () => notifPanel.toggle());
	window.addEventListener(NOTIF_EVENT, refreshBellBadge);

	document.body.appendChild(topBar);
	return topBar;
}

// The sync dot on Account: grey offline · red something failed · amber waiting
// to upload · hidden when everything is in the cloud (or signed out).
async function refreshSyncDot() {
	const dot = ensureTopBar().querySelector(".topbar__sync");
	if (!dot) return;
	const s = await getSyncState().catch(() => null);
	if (!s || !s.signedIn) {
		dot.hidden = true;
		return;
	}
	const [color, label] = !s.online
		? ["#9a9aa2", "Offline — changes will sync later"]
		: s.failed
			? ["#ff5a4a", `${s.failed} change${s.failed === 1 ? "" : "s"} failed to sync — see Account`]
			: s.pending
				? ["#ffd60a", `${s.pending} change${s.pending === 1 ? "" : "s"} waiting to sync`]
				: [null, "All synced"];
	dot.hidden = !color;
	if (color) dot.style.background = color;
	ensureTopBar().querySelector('[data-nav="account"]').setAttribute("aria-label", `Account — ${label}`);
}

// Reflect the unread notification count on the bell badge.
async function refreshBellBadge() {
	const bar = ensureTopBar();
	const badge = bar.querySelector(".topbar__badge");
	if (!badge) return;
	const n = await unreadCount();
	badge.textContent = n > 9 ? "9+" : String(n);
	badge.hidden = n === 0;
}

// Screens that show release dates at a glance; only these are worth re-rendering
// when the daily catalog refresh changed something underneath them.
const RELEASE_SENSITIVE_ROUTES = new Set(["/", "/upcoming", "/recommendations"]);

// Once-a-day housekeeping for a signed-in user: re-check release dates and season
// catalogs against IGDB/TVmaze FIRST, then scan for release-day arrivals (so a
// title postponed overnight doesn't ring the bell), then update the badge. The
// refresh throttles itself to one pass per day; `rerender` lets the boot path
// repaint a release-sensitive screen when the pass changed data.
async function runDailyMaintenance({ rerender = false } = {}) {
	let changed = null;
	try {
		changed = await runDailyCatalogRefresh();
	} catch (err) {
		console.warn("catalog refresh failed", err);
	}
	if (
		rerender &&
		changed &&
		changed.games + changed.catalogs > 0 &&
		RELEASE_SENSITIVE_ROUTES.has(getHashPath().replace(/\/+$/, "") || "/")
	) {
		refreshView();
	}
	try {
		// The notification archive keeps two years (design_documents/history.md).
		await pruneNotifications();
	} catch (err) {
		console.warn("notification prune failed", err);
	}
	try {
		// Comic series subscriptions: new / out-today issues and new trades.
		await runSubscriptionChecks();
	} catch (err) {
		console.warn("comic subscription check failed", err);
	}
	try {
		// "New from …": weekly look at what followed people have made since.
		await runPeopleChecks();
	} catch (err) {
		console.warn("people check failed", err);
	}
	try {
		// Loans whose reminder day has come (roadmap #32).
		const { loanReminders } = await import("./utils/tracking.js");
		const { listAllMedia } = await import("./db/media.js");
		const { addNotification } = await import("./db/notifications.js");
		const { todayDate } = await import("./utils/dates.js");
		for (const n of loanReminders(await listAllMedia(), todayDate())) await addNotification(n);
	} catch (err) {
		console.warn("loan reminders failed", err);
	}
	try {
		// Opt-in: bell notices for news about the library (Newsstand → Setup).
		await runNewsBell();
	} catch (err) {
		console.warn("news bell failed", err);
	}
	try {
		// Phone notifications (roadmap #61): hand the next two weeks to the Worker.
		const { syncPushSchedule } = await import("./db/push.js");
		await syncPushSchedule();
	} catch (err) {
		console.warn("push schedule sync failed", err);
	}
	try {
		await scanReleaseNotifications();
	} catch (err) {
		console.warn("notification scan failed", err);
	}
	refreshBellBadge();
}

// Show the bar only on signed-in app routes (hidden on the auth + migration
// screens), and highlight the active destination.
function updateTopBar(path) {
	const bar = ensureTopBar();
	const show = authed && !isPublicPath(path) && !/^\/migrate\/?$/.test(path);
	bar.hidden = !show;
	// Keep the drawer + notifications panel in step with the bar (e.g. close them
	// on sign-out).
	if (!show) closeDrawer();
	if (!show) notifPanel?.close();
	if (!show) return;
	bar
		.querySelector('[data-nav="account"]')
		.classList.toggle("topbar__btn--active", path.startsWith("/account"));
}

// ---------- Category drawer (left slide-out, opened from the menu button) ----

// The app's media categories. Active ones navigate; the rest are placeholders
// for media types not built yet, shown disabled.
const CATEGORIES = [
	{ id: "videogames", label: "Videogames", route: "/games", active: true },
	{ id: "books", label: "Books", route: "/books", active: true },
	{ id: "tv", label: "Television", route: "/tv", active: true },
	{ id: "anime", label: "Anime", route: "/anime", active: true },
	{ id: "movies", label: "Movies", route: "/movies", active: true },
	{ id: "comics", label: "Comics", route: "/comics", active: true },
	{ id: "music", label: "Music", route: null, active: false },
	{ id: "boardgames", label: "Boardgames", route: null, active: false },
	{ id: "ttrpgs", label: "TTRPGs", route: null, active: false },
];

let drawer = null; // { backdrop, panel }

function ensureDrawer() {
	if (drawer) return drawer;

	function go(route) {
		closeDrawer();
		navigate(route);
	}

	const homeRow = el(
		"button.drawer__item",
		{ type: "button", dataset: { route: "/" }, onclick: () => go("/") },
		el("span.drawer__item-label", {}, "Home"),
	);

	// Suit tint per category — the 4-colour coding used app-wide (drawer dots,
	// covers, stat blocks). Unbuilt categories get a neutral dot.
	const CAT_TINT = {
		videogames: "#8b3dff",
		books: "#ff7a1a",
		tv: "#19d3e8",
		anime: "#ff3d8b",
		movies: "#e5383b",
		comics: "#1fbf5f",
	};
	const categoryRows = CATEGORIES.map((cat) =>
		el(
			`button.drawer__item${cat.active ? "" : ".drawer__item--disabled"}`,
			{
				type: "button",
				disabled: !cat.active,
				dataset: cat.active ? { route: cat.route } : {},
				onclick: cat.active ? () => go(cat.route) : undefined,
			},
			el(
				"span",
				{ style: { display: "flex", alignItems: "center", minWidth: "0" } },
				el("span.drawer__dot", {
					style: {
						width: "9px", height: "9px", borderRadius: "50%", flex: "none",
						background: CAT_TINT[cat.id] ?? "rgba(255,255,255,.2)",
						marginRight: "11px", display: "inline-block",
					},
				}),
				el("span.drawer__item-label", {}, cat.label),
			),
			cat.active ? "" : el("span.drawer__soon", {}, "soon"),
		),
	);

	const panel = el(
		"aside.drawer",
		{ "aria-label": "Categories", "aria-hidden": "true" },
		el("div.drawer__title", {}, "My ", el("em", {}, "Media")),
		homeRow,
		el("div.drawer__section-label", {}, "Categories"),
		...categoryRows,
		// Everything that isn't a category, in one place.
		el("div.drawer__section-label", {}, "More"),
		...[
			["/search", "Search your library"],
			["/news", "Newsstand"],
			["/people", "People"],
			["/history", "History"],
			["/year", "Year in review"],
			["/wanted", "Wishlist & loans"],
			["/groups", "Collections"],
			["/import", "Import"],
			["/settings", "Settings"],
		].map(([route, label]) =>
			el("button.drawer__item", { type: "button", dataset: { route }, onclick: () => go(route) }, el("span.drawer__item-label", {}, label)),
		),
	);

	const backdrop = el("div.drawer-backdrop", {
		hidden: true,
		onclick: closeDrawer,
	});

	document.body.append(backdrop, panel);
	drawer = { backdrop, panel };
	return drawer;
}

function onDrawerKey(e) {
	if (e.key === "Escape") closeDrawer();
}

function openDrawer() {
	const { backdrop, panel } = ensureDrawer();
	// Highlight the drawer item matching the current screen. "/" matches only
	// home exactly; a category (e.g. /games) matches its own sub-paths too.
	const path = getHashPath();
	for (const item of panel.querySelectorAll(".drawer__item")) {
		const route = item.dataset.route;
		const active =
			route === "/" ? path === "/" : route && path.startsWith(route);
		item.classList.toggle("drawer__item--active", !!active);
	}
	backdrop.hidden = false;
	// Next frame so the transition runs from the off-screen (translateX) state.
	requestAnimationFrame(() => panel.classList.add("drawer--open"));
	panel.setAttribute("aria-hidden", "false");
	topBar
		?.querySelector('[data-nav="menu"]')
		?.setAttribute("aria-expanded", "true");
	document.addEventListener("keydown", onDrawerKey);
}

function closeDrawer() {
	if (!drawer) return;
	drawer.panel.classList.remove("drawer--open");
	drawer.panel.setAttribute("aria-hidden", "true");
	drawer.backdrop.hidden = true;
	topBar?.querySelector('[data-nav="menu"]')?.setAttribute("aria-expanded", "false");
	document.removeEventListener("keydown", onDrawerKey);
}

export function navigate(path) {
	window.location.hash = path.startsWith("/") ? path : `/${path}`;
}

// Swap the current URL in place (no re-render, no new Back step) — for state a
// view wants Back to return to, e.g. the Newsstand page you were on.
export function replaceRoute(path) {
	history.replaceState(history.state, "", `#${path}`);
	if (navStack.length) navStack[navStack.length - 1] = path;
}

window.addEventListener("hashchange", dispatch);

// Tier-1 skin changes reapply via CSS the instant `data-skin` flips (handled in
// setSkin). Tier-2 screens render an alternate *layout* per skin (e.g. the
// magazine Home), so re-dispatch the current route to rebuild it. Views fully
// rebuild on each navigation, so no teardown/diffing is needed.
window.addEventListener("skinchange", dispatch);

// A password-reset link lands on the app root with the recovery token in the URL
// hash (`#access_token=…&type=recovery`). That's not a real route — supabase-js
// (detectSessionInUrl) consumes the fragment and emits a PASSWORD_RECOVERY event,
// which we route to /reset-password below.
function isRecoveryLink() {
	const raw = window.location.hash;
	return raw.includes("type=recovery") || raw.includes("access_token=");
}

// Re-render the current route from the (now-updated) local cache. The cloud sync
// engine calls this after a startup/sign-in pull that changed data, so the screen
// in front of the user reflects what was just fetched.
function refreshView() {
	dispatch();
}

// Rebuild the current route in place (navStack untouched) — for an in-place data
// change a view can't patch itself, e.g. a re-link swapping the catalog under it.
export function rerenderRoute() {
	dispatch();
}

window.addEventListener("DOMContentLoaded", async () => {
	// Re-assert the skin from storage (the index.html inline script already set it
	// pre-paint; this keeps things correct even if that script is ever changed).
	// Keep an on-device log of errors/warnings first (Account → Diagnostics).
	installDiagnostics();
	// Keyboard access for the app's button-like rows (roadmap #64).
	installButtonRoles();
	// "People you follow" options for the list filters (needs the DB, not the network).
	loadPersonFacets().catch((err) => console.warn("person facets failed", err));
	loadTagFacets().catch((err) => console.warn("tag facets failed", err));
	loadAiringFacets().catch((err) => console.warn("airing facets failed", err));
	// Pull down at the top of a screen to sync now and redraw (roadmap #22).
	installPullToRefresh(async () => {
		await syncNow();
		rerenderRoute();
	});
	applySkinOnBoot();

	onAuthChange((event, session) => {
		authed = !!session;
		if (event === "PASSWORD_RECOVERY") {
			navigate("/reset-password");
			return;
		}
		// Enforce the gate when the session ends (sign-out or expiry) while the
		// user is on a protected screen. Sign-in is handled by the auth views.
		if (event === "SIGNED_OUT" && !isPublicPath(getHashPath())) {
			navigate("/sign-in");
		}
		// A fresh sign-in (without a page reload) should run the daily housekeeping
		// too (catalog refresh + release-day scan); the boot path only runs it for
		// an already-present session.
		if (event === "SIGNED_IN") {
			runDailyMaintenance({ rerender: true });
		}
	});

	// Boot the cloud sync engine (no-op until signed in).
	setRefreshView(refreshView);
	initSync();

	if (isRecoveryLink()) {
		// Don't dispatch the token fragment as a route. Wait for the
		// PASSWORD_RECOVERY event to route us; fall back to sign-in if the link
		// is stale and no session materializes.
		setTimeout(() => {
			if (isRecoveryLink()) navigate("/sign-in");
		}, 2500);
		return;
	}

	// Seed auth state before the first (gated) dispatch.
	const session = await getSession();
	authed = !!session;

	if (!window.location.hash) {
		window.location.hash = "/";
	}
	dispatch();

	// After the first screen is up, show release notes once per upgrade — but only
	// to signed-in users, so it never covers the sign-in / sign-up screens.
	if (authed) {
		maybeShowWhatsNew();
		// Reflect existing unread on the bell, then run the daily housekeeping
		// (release-date / season-catalog refresh + release-day scan).
		refreshBellBadge();
		runDailyMaintenance({ rerender: true });
	}

	// A PWA can sit open for days: when it comes back to the foreground on a new
	// day, run the housekeeping again (a no-op once today's pass has run).
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState !== "visible" || !authed) return;
		if (!dailyRefreshDue()) return;
		runDailyMaintenance();
	});
});

if ("serviceWorker" in navigator && import.meta.env.PROD) {
	window.addEventListener("load", () => {
		navigator.serviceWorker
			.register("/service-worker.js")
			.catch((err) => console.warn("Service worker registration failed:", err));
	});
}

// This app renders by clearing #app on each navigation; it has no HMR boundary,
// so a hot update would otherwise leave stale, stacked DOM. Force a clean full
// reload on any update during dev. Stripped from production builds.
if (import.meta.hot) {
	import.meta.hot.accept(() => window.location.reload());
}
